import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calendar, FileText, MapPin, Search, X, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_BADGE = {
  'Naissance':    'border-green-400 text-green-700',
  'Décès':        'border-stone-400 text-stone-500',
  'Sépulture':    'border-stone-300 text-stone-400',
  'Mariage':      'border-rose-400 text-rose-600',
  'Mariage civil':'border-rose-400 text-rose-600',
  'Fiançailles':  'border-pink-300 text-pink-500',
  'Divorce':      'border-orange-400 text-orange-600',
  'Baptême':      'border-blue-300 text-blue-500',
};

const badgeClass = (type) =>
  `border text-xs font-semibold px-2 py-0.5 rounded-sm shrink-0 ${
    TYPE_BADGE[type] || 'border-stone-300 text-stone-500'
  }`;

const fullName = (p) => `${p.given_name || ''} ${p.surname || ''}`.trim() || '—';

const PersonsList = ({ persons, typeValue, year, navigate }) => {
  if (!persons || persons.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
      <Users size={13} style={{ color: 'var(--text-muted)' }} className="shrink-0" />
      {persons.map((p, i) => (
        <span key={i} className="flex items-center gap-1">
          <button
            onClick={() => p.handle && navigate(`/persons/${p.handle}`)}
            className="text-sm font-medium hover:underline"
            style={{ color: 'var(--forest)' }}
          >
            {fullName(p)}
          </button>
          {/* Âge au décès */}
          {typeValue === 13 && p.age_at_death != null && (
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              ({p.age_at_death} ans)
            </span>
          )}
          {i < persons.length - 1 && (
            <span style={{ color: 'var(--text-muted)' }}>&</span>
          )}
        </span>
      ))}
    </div>
  );
};

export const EventsPage = () => {
  const [events,    setEvents]    = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [typeFilter,setTypeFilter]= useState('');
  const navigate = useNavigate();

  useEffect(() => { fetchEvents(); }, []);

  useEffect(() => {
    let result = events;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        (e.event_type  || '').toLowerCase().includes(q) ||
        (e.place_name  || '').toLowerCase().includes(q) ||
        (e.date_str    || '').includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.persons || []).some(p => fullName(p).toLowerCase().includes(q))
      );
    }
    if (typeFilter) {
      result = result.filter(e => e.event_type === typeFilter);
    }
    setFiltered(result);
  }, [search, typeFilter, events]);

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/events`, { withCredentials: true });
      setEvents(data);
      setFiltered(data);

    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const eventTypes = [...new Set(events.map(e => e.event_type).filter(Boolean))].sort();

  return (
    <div data-testid="events-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold mb-1"
          style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
          Événements
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          {loading ? 'Chargement…'
            : `${filtered.length} événement${filtered.length > 1 ? 's' : ''} — du plus récent au plus ancien`}
        </p>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }} />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, lieu, date…" className="pl-9 pr-8" />
          {search && (
            <button onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--text-muted)' }}>
              <X size={14} />
            </button>
          )}
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2 text-sm border rounded-sm"
          style={{
            borderColor: 'var(--border)', backgroundColor: 'var(--bg-input)',
            color: 'var(--text-primary)', minWidth: '180px',
          }}>
          <option value="">Tous les types</option>
          {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Aucun événement trouvé</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((ev, i) => (
            <div key={ev.handle || i}
              className="border p-4 flex items-start gap-4 transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>

              {/* Badge type */}
              <div className={badgeClass(ev.event_type)}
                style={{ minWidth: '90px', textAlign: 'center', marginTop: '2px' }}>
                {ev.event_type}
              </div>

              {/* Contenu */}
              <div className="flex-1 min-w-0">
                {/* Date + lieu */}
                <div className="flex flex-wrap items-center gap-3">
                  {ev.date_str && (
                    <span className="flex items-center gap-1 text-sm font-medium"
                      style={{ color: 'var(--text-primary)' }}>
                      <Calendar size={13} style={{ color: 'var(--terracotta)' }} />
                      {ev.date_str}
                    </span>
                  )}
                  {ev.place_name && (
                    <span className="flex items-center gap-1 text-sm"
                      style={{ color: 'var(--text-secondary)' }}>
                      <MapPin size={13} style={{ color: 'var(--forest)' }} />
                      {ev.place_name}
                    </span>
                  )}
                </div>

                {/* Personnes impliquées */}
                <PersonsList
                  persons={ev.persons}
                  typeValue={ev.type_value}
                  year={ev.year}
                  navigate={navigate}
                />

                {ev.description && (
                  <p className="text-xs mt-1 italic" style={{ color: 'var(--text-muted)' }}>
                    {ev.description}
                  </p>
                )}
              </div>

              {/* ID Gramps */}
              {ev.gramps_id && (
                <span className="text-xs shrink-0 hidden sm:block"
                  style={{ color: 'var(--text-muted)' }}>
                  {ev.gramps_id}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};